(() => {
  if (customElements.get('wei-quote-form')) return;

  const storageKey = 'wei:bulk-quote:product:v1';
  const draftLifetime = 30 * 60 * 1000;
  const storedFields = ['product', 'variant', 'quantity'];
  const cleanText = (value, limit = 300) => String(value || '').replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, limit);
  const validQuantity = (value) => /^\d+$/.test(value) && Number.isSafeInteger(Number(value)) && Number(value) >= 1;

  class WeiQuoteForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('form');
      this.fields = Array.from(this.querySelectorAll('[data-quote-field]'));
      if (!this.form) return;
      // Native required/email/number constraints remain the no-JS fallback.
      // Once enhanced, this component owns the inline errors and focus path.
      this.form.noValidate = true;
      if (this.querySelector('[data-quote-success]')) {
        this.clearDraft();
        return;
      }

      this.submitButton = this.querySelector('[data-quote-submit]');
      this.liveStatus = this.querySelector('[data-quote-live]');
      this.errorSummary = this.querySelector('[data-quote-errors]');
      this.errorHeading = this.querySelector('[data-quote-error-heading]');
      this.hasServerError = Boolean(this.querySelector('[data-quote-server-errors]'));
      this.prefill();
      this.restoreDraft();
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('input', this.onInput);
      this.form.addEventListener('focusout', this.onFocusOut);
      window.addEventListener('pageshow', this.onPageShow);
      this.fields.filter((field) => field.tagName === 'TEXTAREA').forEach(this.growTextarea);
      if (this.hasServerError) this.errorSummary.focus();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.form?.removeEventListener('input', this.onInput);
      this.form?.removeEventListener('focusout', this.onFocusOut);
      window.removeEventListener('pageshow', this.onPageShow);
      this.initialized = false;
    }

    field(name) { return this.fields.find((field) => field.dataset.quoteField === name); }

    prefill() {
      if (this.hasServerError) return;
      const params = new URLSearchParams(window.location.search);
      ['product', 'variant'].forEach((key) => {
        const field = this.field(key);
        if (field && !field.value && params.has(key)) field.value = cleanText(params.get(key));
      });
      const quantity = params.get('quantity') || '';
      if (validQuantity(quantity) && !this.field('quantity').value) this.field('quantity').value = quantity;
      const variant = this.field('variant').value;
      if (variant && variant !== 'Default Title' && !this.field('size-color').value) this.field('size-color').value = variant;
      if (params.has('url')) {
        try {
          const url = new URL(params.get('url'), window.location.origin);
          if (url.origin === window.location.origin && /\/products\/[^/]+/.test(url.pathname)) {
            const variantId = url.searchParams.get('variant');
            url.search = '';
            url.hash = '';
            if (/^\d+$/.test(variantId || '')) url.searchParams.set('variant', variantId);
            this.field('url').value = url.href;
          }
        } catch (_) {}
      }
    }

    saveDraft() {
      const values = Object.fromEntries(storedFields.map((key) => [key, cleanText(this.field(key)?.value)]));
      try { sessionStorage.setItem(storageKey, JSON.stringify({ createdAt: Date.now(), values })); } catch (_) {}
    }

    restoreDraft() {
      try {
        const draft = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        if (!draft) return;
        if (!draft.createdAt || Date.now() - draft.createdAt > draftLifetime) {
          this.clearDraft();
          return;
        }
        if (!this.hasServerError) return;
        storedFields.forEach((key) => {
          const field = this.field(key);
          const value = cleanText(draft.values?.[key]);
          if (field && !field.value && (key !== 'quantity' || validQuantity(value))) field.value = value;
        });
        this.liveStatus.textContent = this.dataset.restoredMessage;
      } catch (_) { this.clearDraft(); }
    }

    clearDraft() {
      try { sessionStorage.removeItem(storageKey); } catch (_) {}
    }

    validateField(field) {
      if (!field || field.type === 'hidden') return true;
      let message = '';
      const value = field.value.trim();
      if (field.required && !value) message = this.dataset.requiredMessage;
      else if (field.type === 'email' && value && field.validity.typeMismatch) message = this.dataset.emailMessage;
      else if (field.dataset.quoteField === 'quantity' && !validQuantity(value)) message = this.dataset.quantityMessage;
      else if (!field.validity.valid) message = this.dataset.invalidMessage;
      const error = field.closest('.wei-quote__field')?.querySelector('[data-field-error]');
      if (error) error.textContent = message;
      if (message) field.setAttribute('aria-invalid', 'true');
      else field.removeAttribute('aria-invalid');
      return !message;
    }

    onSubmit = (event) => {
      if (this.submitting) {
        event.preventDefault();
        return;
      }
      let firstInvalid = null;
      this.fields.forEach((field) => {
        if (!this.validateField(field) && !firstInvalid) firstInvalid = field;
      });
      if (firstInvalid) {
        event.preventDefault();
        this.errorSummary.hidden = false;
        this.errorHeading.textContent = this.dataset.errorSummary;
        firstInvalid.focus();
        return;
      }
      if (navigator.onLine === false) {
        event.preventDefault();
        this.errorSummary.hidden = false;
        this.errorHeading.textContent = this.dataset.offlineMessage;
        this.errorSummary.focus();
        return;
      }
      this.saveDraft();
      queueMicrotask(() => {
        if (event.defaultPrevented || !this.isConnected) return;
        this.submitting = true;
        this.form.setAttribute('aria-busy', 'true');
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.errorSummary.hidden = true;
        this.liveStatus.textContent = this.dataset.sendingMessage;
      });
    };

    onInput = (event) => {
      const field = event.target;
      if (!field.matches('[data-quote-field]')) return;
      if (field.hasAttribute('aria-invalid')) this.validateField(field);
      if (field.tagName === 'TEXTAREA') this.growTextarea(field);
    };

    onFocusOut = (event) => {
      if (event.target.matches('[data-quote-field]') && event.target.value) this.validateField(event.target);
    };

    growTextarea = (field) => {
      field.style.height = 'auto';
      field.style.height = `${Math.max(field.scrollHeight + 2, 150)}px`;
    };

    onPageShow = () => {
      if (!this.submitting) return;
      this.submitting = false;
      this.form.removeAttribute('aria-busy');
      this.submitButton.removeAttribute('aria-disabled');
      this.liveStatus.textContent = '';
    };
  }

  class WeiProductQuote extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('[data-quote-link]');
      this.productInfo = this.closest('product-info') || this.parentElement;
      if (!this.link || !this.productInfo) return;
      this.link.addEventListener('click', this.updateLink);
      this.link.addEventListener('focus', this.updateLink);
      this.link.addEventListener('pointerdown', this.updateLink);
      this.productInfo.addEventListener('input', this.updateLink);
      this.productInfo.addEventListener('change', this.updateLink);
      this.observer = new MutationObserver(this.updateLink);
      this.observer.observe(this.productInfo, { childList: true, subtree: true });
      this.updateLink();
    }

    disconnectedCallback() {
      this.link?.removeEventListener('click', this.updateLink);
      this.link?.removeEventListener('focus', this.updateLink);
      this.link?.removeEventListener('pointerdown', this.updateLink);
      this.productInfo?.removeEventListener('input', this.updateLink);
      this.productInfo?.removeEventListener('change', this.updateLink);
      this.observer?.disconnect();
    }

    updateLink = () => {
      const productForm = document.getElementById(this.dataset.productFormId);
      let variantId = productForm?.elements.namedItem('id')?.value || this.dataset.variantId;
      const quantityInput = productForm?.elements.namedItem('quantity') || this.productInfo.querySelector('input[name="quantity"]');
      const quantity = String(quantityInput?.value || '1');
      let variantTitle = this.dataset.variantTitle;
      const picker = this.productInfo.querySelector('variant-selects');
      const selectedOptions = Array.from(picker?.querySelectorAll('select, input[type="radio"]:checked') || []).map((field) => field.value);
      if (selectedOptions.length) variantTitle = selectedOptions.join(' / ');
      const variantJson = picker?.querySelector('[data-selected-variant]');
      if (variantJson) {
        try {
          const variant = JSON.parse(variantJson.textContent);
          if (!variant || (selectedOptions.length && variant.title !== variantTitle)) variantId = '';
          else {
            variantId = String(variant.id);
            variantTitle = variant.title;
          }
        } catch (_) {}
      }
      const quoteUrl = new URL(this.dataset.quotePath, window.location.origin);
      const productUrl = new URL(this.dataset.productUrl, window.location.origin);
      if (/^\d+$/.test(variantId || '')) productUrl.searchParams.set('variant', variantId);
      quoteUrl.searchParams.set('product', cleanText(this.dataset.productTitle));
      quoteUrl.searchParams.set('variant', cleanText(variantTitle));
      quoteUrl.searchParams.set('url', productUrl.href);
      quoteUrl.searchParams.set('quantity', validQuantity(quantity) ? quantity : '1');
      this.link.href = quoteUrl.href;
    };
  }

  customElements.define('wei-quote-form', WeiQuoteForm);
  customElements.define('wei-product-quote', WeiProductQuote);
})();
